import { useAuthRuntime } from "@frontend/components/auth/auth-runtime"
import { useCallback, useEffect, useMemo, useRef } from "react"

export interface SceneSeriesItem {
  id: string
  series_identifier: string
  series_description: string | null
  modality: string | null
  image_plane: string | null
  series_number: number | null
  number_of_frames: number | null
  voxel_size_z: number | null
}

interface CachedVolume {
  url: string
  name: string
  refCount: number
}

export function useViewerSceneSeries(caseId: string, series: SceneSeriesItem[]) {
  const { getAccessToken } = useAuthRuntime()
  const cacheRef = useRef<Map<string, CachedVolume>>(new Map())
  const viewerAssignmentsRef = useRef<Map<number, string>>(new Map())

  const seriesById = useMemo(() => {
    const map = new Map<string, SceneSeriesItem>()
    for (const item of series) {
      map.set(item.id, item)
    }
    return map
  }, [series])

  const releaseSeries = useCallback((seriesId: string) => {
    const entry = cacheRef.current.get(seriesId)
    if (!entry) return
    entry.refCount -= 1
    if (entry.refCount <= 0) {
      URL.revokeObjectURL(entry.url)
      cacheRef.current.delete(seriesId)
    }
  }, [])

  const releaseViewer = useCallback(
    (viewerIndex: number) => {
      const assigned = viewerAssignmentsRef.current.get(viewerIndex)
      if (!assigned) return
      viewerAssignmentsRef.current.delete(viewerIndex)
      releaseSeries(assigned)
    },
    [releaseSeries]
  )

  const clearAllViewers = useCallback(() => {
    for (const [viewerIndex] of viewerAssignmentsRef.current) {
      releaseViewer(viewerIndex)
    }
  }, [releaseViewer])

  const releaseViewersOutsideRange = useCallback(
    (viewerCount: number) => {
      for (const [viewerIndex] of viewerAssignmentsRef.current) {
        if (viewerIndex >= viewerCount) {
          releaseViewer(viewerIndex)
        }
      }
    },
    [releaseViewer]
  )

  const getAssignedSeriesId = useCallback((viewerIndex: number) => {
    return viewerAssignmentsRef.current.get(viewerIndex)
  }, [])

  const acquireVolumeForViewer = useCallback(
    async (viewerIndex: number, seriesId: string) => {
      const targetSeries = seriesById.get(seriesId)
      if (!targetSeries) {
        throw new Error("Selected series is not available")
      }

      const alreadyAssigned = viewerAssignmentsRef.current.get(viewerIndex)
      if (alreadyAssigned === seriesId) {
        const existing = cacheRef.current.get(seriesId)
        if (existing) {
          return { url: existing.url, name: existing.name }
        }
      }

      releaseViewer(viewerIndex)

      let entry = cacheRef.current.get(seriesId)
      if (!entry) {
        const token = await getAccessToken()
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined
        const response = await fetch(`/api/cases/${caseId}/series/${seriesId}/file`, {
          headers
        })
        if (!response.ok) {
          throw new Error(`Failed to load series file (${response.status})`)
        }
        const blob = await response.blob()
        const objectUrl = URL.createObjectURL(blob)
        const baseName = targetSeries.series_description ?? targetSeries.series_identifier
        entry = {
          url: objectUrl,
          name: baseName.endsWith(".nii.gz") || baseName.endsWith(".nii") ? baseName : `${baseName}.nii.gz`,
          refCount: 0
        }
        cacheRef.current.set(seriesId, entry)
      }

      entry.refCount += 1
      viewerAssignmentsRef.current.set(viewerIndex, seriesId)

      return {
        url: entry.url,
        name: entry.name
      }
    },
    [caseId, getAccessToken, releaseViewer, seriesById]
  )

  useEffect(() => {
    return () => {
      clearAllViewers()
      for (const entry of cacheRef.current.values()) {
        URL.revokeObjectURL(entry.url)
      }
      cacheRef.current.clear()
      viewerAssignmentsRef.current.clear()
    }
  }, [clearAllViewers])

  return {
    acquireVolumeForViewer,
    getAssignedSeriesId,
    releaseViewer,
    clearAllViewers,
    releaseViewersOutsideRange
  }
}
